import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Send, Search, MoreVertical, Phone, Video, 
  Image, Paperclip, Smile, Check, CheckCheck, Wifi, WifiOff, Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace("https://", "wss://").replace("http://", "ws://");
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function Chat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileList, setShowMobileList] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Get user from URL params or from conversation
  const targetUserId = searchParams.get("userId");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch messages for a specific user
  const fetchMessages = useCallback(async (otherUserId) => {
    if (!user) return;
    setLoadingMessages(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/chat/messages/${otherUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  }, [user]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!user) return;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/chat/${user.user_id}`);
        
        ws.onopen = () => {
          console.log("WebSocket connected");
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          
          if (data.type === "new_message") {
            // Add new message to the list if it's for the current conversation
            if (selectedConversation && 
                (data.message.sender_id === selectedConversation.user.user_id ||
                 data.message.receiver_id === selectedConversation.user.user_id)) {
              setMessages(prev => [...prev, data.message]);
            }
            // Update conversations list
            fetchConversations();
          } else if (data.type === "message_sent") {
            // Update message status
            setMessages(prev => prev.map(msg => 
              msg.message_id === data.message.message_id 
                ? { ...msg, status: "delivered" }
                : msg
            ));
          } else if (data.type === "messages_read") {
            // Update message read status
            setMessages(prev => prev.map(msg => 
              msg.sender_id === user.user_id && msg.status !== "read"
                ? { ...msg, status: "read" }
                : msg
            ));
          } else if (data.type === "user_typing") {
            // Could show typing indicator here
          }
        };
        
        ws.onclose = () => {
          console.log("WebSocket disconnected");
          setWsConnected(false);
          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
        };
        
        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
        
        wsRef.current = ws;
      } catch (error) {
        console.error("WebSocket connection error:", error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, selectedConversation, fetchConversations]);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle target user from URL
  useEffect(() => {
    if (targetUserId && user && !loading) {
      const existingConv = conversations.find(c => c.user.user_id === targetUserId);
      if (existingConv) {
        handleSelectConversation(existingConv);
      } else {
        // Create new conversation entry
        createNewConversation(targetUserId);
      }
    }
  }, [targetUserId, user, conversations, loading]);

  const createNewConversation = async (otherUserId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/chat/user/${otherUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        const newConv = {
          conversation_id: `conv_${user.user_id}_${otherUserId}`,
          user: {
            user_id: userData.user_id,
            name: userData.name,
            role: userData.role,
            is_online: userData.is_online
          },
          last_message: null,
          unread_count: 0
        };
        setSelectedConversation(newConv);
        setShowMobileList(false);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error("Could not start conversation");
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    const messageContent = newMessage.trim();
    setNewMessage("");
    
    // Send via WebSocket if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "message",
        receiver_id: selectedConversation.user.user_id,
        content: messageContent,
        message_type: "text"
      }));
      
      // Optimistically add message
      const optimisticMessage = {
        message_id: `temp_${Date.now()}`,
        sender_id: user.user_id,
        receiver_id: selectedConversation.user.user_id,
        content: messageContent,
        status: "sending",
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, optimisticMessage]);
    } else {
      // Fallback to REST API
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/chat/messages`, {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            receiver_id: selectedConversation.user.user_id,
            content: messageContent,
            message_type: "text"
          })
        });
        
        if (response.ok) {
          const sentMessage = await response.json();
          setMessages(prev => [...prev, sentMessage]);
        }
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message");
      }
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setShowMobileList(false);
    
    // Fetch messages for this conversation
    await fetchMessages(conv.user.user_id);
    
    // Mark as read locally
    setConversations(prev => 
      prev.map(c => c.conversation_id === conv.conversation_id 
        ? { ...c, unread_count: 0 } 
        : c
      )
    );
  };

  const filteredConversations = conversations.filter(c => 
    c.user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getMessageStatus = (msg) => {
    if (msg.sender_id !== user?.user_id) return null;
    
    if (msg.status === "read") {
      return <CheckCheck className="w-3 h-3 text-blue-400" />;
    } else if (msg.status === "delivered" || msg.status === "sent") {
      return <CheckCheck className="w-3 h-3" />;
    } else if (msg.status === "sending") {
      return <Loader2 className="w-3 h-3 animate-spin" />;
    }
    return <Check className="w-3 h-3" />;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/talent/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <span className="font-display text-lg font-bold text-gray-900">Messages</span>
              {totalUnread > 0 && (
                <Badge className="bg-violet-600 text-white text-xs">{totalUnread}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <Badge variant="outline" className="text-green-600 border-green-300">
                <Wifi className="w-3 h-3 mr-1" />Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400 border-gray-200">
                <WifiOff className="w-3 h-3 mr-1" />Offline
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main Chat Area */}
      <div className="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">
        {/* Conversations List */}
        <div className={`w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col ${
          !showMobileList && selectedConversation ? 'hidden md:flex' : 'flex'
        }`}>
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="search-conversations"
              />
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="font-medium">No conversations yet</p>
                <p className="text-sm mt-1">Start a chat from someone&apos;s profile</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full p-3 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                    selectedConversation?.conversation_id === conv.conversation_id ? 'bg-violet-50' : ''
                  }`}
                  data-testid={`conversation-${conv.user?.user_id}`}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                      {conv.user?.name?.charAt(0) || "?"}
                    </div>
                    {conv.user?.is_online && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 text-sm truncate">{conv.user?.name}</h4>
                      <span className="text-xs text-gray-400">{formatTime(conv.last_message?.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conv.last_message?.content || "No messages yet"}</p>
                    <Badge variant="outline" className="text-xs mt-1 capitalize">{conv.user?.role}</Badge>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-violet-600 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                      {conv.unread_count}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Window */}
        <div className={`flex-1 flex flex-col bg-gray-50 ${
          showMobileList && !selectedConversation ? 'hidden md:flex' : 'flex'
        }`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden"
                    onClick={() => setShowMobileList(true)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                      {selectedConversation.user?.name?.charAt(0) || "?"}
                    </div>
                    {selectedConversation.user?.is_online && (
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{selectedConversation.user?.name}</h4>
                    <span className="text-xs text-gray-500">
                      {selectedConversation.user?.is_online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="text-gray-500">
                    <Phone className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-gray-500">
                    <Video className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-gray-500">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <p className="text-sm">Start the conversation by sending a message</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                          msg.sender_id === user?.user_id
                            ? 'bg-violet-600 text-white rounded-br-md'
                            : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          msg.sender_id === user?.user_id ? 'text-violet-200' : 'text-gray-400'
                        }`}>
                          <span className="text-xs">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {getMessageStatus(msg)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="bg-white border-t border-gray-200 p-3">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="text-gray-500 flex-shrink-0">
                    <Paperclip className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-gray-500 flex-shrink-0">
                    <Image className="w-5 h-5" />
                  </Button>
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button variant="ghost" size="icon" className="text-gray-500 flex-shrink-0">
                    <Smile className="w-5 h-5" />
                  </Button>
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    className="bg-violet-600 hover:bg-violet-700 flex-shrink-0"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Select a conversation</h3>
                <p className="text-gray-500 text-sm">Choose from your existing conversations or start a new one from a profile</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
